import { useState, useEffect, useRef, useCallback } from 'react';
import './Dashboard.css';

const UNICATOR_HOST = 'https://unicator.stendahls.dev';
const ENDPOINT_URL = `${UNICATOR_HOST}/graphql?query=%7B%0A%20%20devices%20%7B%0A%20%20%20%20hostname%0A%20%20%20%20identity%0A%20%20%20%20lastSeen%0A%20%20%20%20ap%20%7B%0A%20%20%20%20%20%20location%0A%20%20%20%20%7D%0A%20%20%7D%0A%7D%0A`;
const PEOPLE_ENDPOINT_URL = `${UNICATOR_HOST}/graphql?query=%7B%0A%20%20people%20%7B%0A%20%20%20%20displayName%0A%20%20%20%20email%0A%20%20%7D%0A%7D%0A`;
const POLL_INTERVAL = 5000; // 5 seconds
const MAX_LOG_ENTRIES = 100;

function Dashboard() {
    const [error, setError] = useState(null);
    const [peopleCount, setPeopleCount] = useState(0);
    const [lastUpdate, setLastUpdate] = useState('Never');
    const [floorTotals, setFloorTotals] = useState(new Map());
    const [eventLog, setEventLog] = useState([]);
    const [connectedPeople, setConnectedPeople] = useState(new Map());
    const [floorsData, setFloorsData] = useState(new Map());
    const [nameLookup, setNameLookup] = useState(new Map());
    const [recentlyMoved, setRecentlyMoved] = useState(new Set());
    const [urlParams] = useState(new URLSearchParams(window.location.search));
    const [ttsEnabled, setTtsEnabled] = useState(false);

    const previousPeopleRef = useRef(new Map());
    const peoplePollIntervalRef = useRef(null);
    const devicesPollIntervalRef = useRef(null);
    const addEventRef = useRef(null);
    const detectChangesRef = useRef(null);
    const updateDisplayRef = useRef(null);
    const updateEventNamesRef = useRef(null);
    const isFetchingDevicesRef = useRef(false);
    const isFetchingPeopleRef = useRef(false);
    const ttsEnabledRef = useRef(false);

    // Format time
    const formatTime = (date = new Date()) => {
        return date.toLocaleTimeString('sv-SE', { hour12: false });
    };

    const formatFullTime = (date = new Date()) => {
        return date.toLocaleString('sv-SE', {
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Format person name
    const formatPersonName = useCallback((identity) => {
        if (nameLookup.has(identity)) {
            return nameLookup.get(identity);
        }
        // Fallback to original dashboard formatting
        return identity.split('.').map(part =>
            part.charAt(0).toUpperCase() + part.slice(1)
        ).join(' ');
    }, [nameLookup]);

    // Extract floor number from location
    const extractFloor = (location) => {
        if (!location || typeof location !== 'string') {
            return null;
        }
        const match = location.match(/^(\d)/);
        return match ? parseInt(match[1], 10) : null;
    };

    // Calculate floor totals
    const calculateFloorTotals = useCallback((currentPeople) => {
        const totals = new Map();
        currentPeople.forEach((info, identity) => {
            const floor = extractFloor(info.location);
            if (floor !== null) {
                totals.set(floor, (totals.get(floor) || 0) + 1);
            }
        });
        return totals;
    }, []);

    // Speak event using text-to-speech
    const speakEvent = useCallback((event) => {
        if (!ttsEnabledRef.current) return;

        let text = '';
        if (event.type === 'connected') {
            text = `${event.name} connected`;
        } else if (event.type === 'disconnected') {
            text = `${event.name} disconnected`;
        } else if (event.type === 'moved') {
            text = `${event.name} moved from floor ${event.fromFloor} to floor ${event.toFloor}`;
        }

        if (text && 'speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;
            window.speechSynthesis.speak(utterance);
        }
    }, []);

    // Keep ref updated
    useEffect(() => {
        ttsEnabledRef.current = ttsEnabled;
    }, [ttsEnabled]);

    // Add event to log
    const addEvent = useCallback((type, identity, fromFloor = null, toFloor = null) => {
        const now = new Date();
        const event = {
            type,
            identity,
            name: formatPersonName(identity),
            time: formatTime(now),
            fullTime: formatFullTime(now),
            timestamp: now,
            fromFloor,
            toFloor
        };

        setEventLog(prev => {
            const newLog = [...prev, event];
            if (newLog.length > MAX_LOG_ENTRIES) {
                return newLog.slice(-MAX_LOG_ENTRIES);
            }
            return newLog;
        });

        // Speak the event if TTS is enabled
        speakEvent(event);
    }, [formatPersonName, speakEvent]);

    // Keep refs updated
    addEventRef.current = addEvent;

    // Extract unique people from devices
    const extractPeople = (devices) => {
        const people = new Map();
        devices.forEach(device => {
            if (device.identity && typeof device.identity === 'string' && device.identity.trim()) {
                const identity = device.identity.trim();
                const location = device.ap?.location || null;
                const lastSeen = device.lastSeen || null;

                let lastSeenTimestamp = null;
                if (lastSeen) {
                    const parsed = new Date(lastSeen).getTime();
                    if (!isNaN(parsed)) {
                        lastSeenTimestamp = parsed;
                    }
                }

                if (!people.has(identity)) {
                    people.set(identity, { location, lastSeen: lastSeenTimestamp });
                } else {
                    const existing = people.get(identity);
                    const existingLastSeen = existing.lastSeen;

                    if (lastSeenTimestamp !== null) {
                        if (existingLastSeen === null || lastSeenTimestamp > existingLastSeen) {
                            people.set(identity, { location, lastSeen: lastSeenTimestamp });
                        }
                    }
                }
            }
        });
        return people;
    };

    // Fetch people data
    const fetchPeople = async () => {
        if (isFetchingPeopleRef.current) {
            return null;
        }
        isFetchingPeopleRef.current = true;
        try {
            const response = await fetch(PEOPLE_ENDPOINT_URL);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonData = await response.json();
            const people = jsonData.data?.people || [];

            const lookup = new Map();
            people.forEach(person => {
                if (person.email && person.displayName) {
                    lookup.set(person.email, person.displayName);
                    const username = person.email.split('@')[0];
                    if (username) {
                        lookup.set(username, person.displayName);
                    }
                }
            });

            setNameLookup(lookup);
            return people;
        } catch (error) {
            console.error('Error fetching people:', error);
            return null;
        } finally {
            isFetchingPeopleRef.current = false;
        }
    };

    // Fetch devices
    const fetchDevices = async () => {
        if (isFetchingDevicesRef.current) {
            return null;
        }
        isFetchingDevicesRef.current = true;
        try {
            const response = await fetch(ENDPOINT_URL);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonData = await response.json();
            return jsonData.data?.devices || [];
        } catch (error) {
            console.error('Error fetching devices:', error);
            return null;
        } finally {
            isFetchingDevicesRef.current = false;
        }
    };

    // Update entire display
    const updateDisplay = useCallback((currentPeople, movedIdentities = new Set()) => {
        const count = currentPeople.size;
        setPeopleCount(count);
        setLastUpdate(formatFullTime());

        const totals = calculateFloorTotals(currentPeople);
        setFloorTotals(totals);

        setConnectedPeople(currentPeople);

        // Update floors data
        const floorGroups = new Map();
        currentPeople.forEach((info, identity) => {
            const floor = extractFloor(info.location);
            if (floor !== null && floor >= 1 && floor <= 6) {
                if (!floorGroups.has(floor)) {
                    floorGroups.set(floor, []);
                }
                floorGroups.get(floor).push([identity, info]);
            }
        });

        // Sort people within each floor
        floorGroups.forEach((people, floor) => {
            people.sort((a, b) => a[0].localeCompare(b[0]));
        });

        setFloorsData(floorGroups);

        // Set recently moved people for animation
        if (movedIdentities.size > 0) {
            setRecentlyMoved(new Set(movedIdentities));
            // Clear animation after animation duration
            setTimeout(() => {
                setRecentlyMoved(new Set());
            }, 2000);
        }
    }, [calculateFloorTotals]);

    // Keep refs updated
    updateDisplayRef.current = updateDisplay;

    // Detect changes
    const detectChanges = useCallback((currentDevices) => {
        const currentPeople = extractPeople(currentDevices);
        const previousPeople = previousPeopleRef.current;
        const movedIdentities = new Set();

        // Detect disconnections
        previousPeople.forEach((info, identity) => {
            if (!currentPeople.has(identity)) {
                addEventRef.current('disconnected', identity);
            }
        });

        // Detect connections and floor changes
        currentPeople.forEach((currentInfo, identity) => {
            if (!previousPeople.has(identity)) {
                addEventRef.current('connected', identity);
            } else {
                const previousInfo = previousPeople.get(identity);
                const previousFloor = extractFloor(previousInfo.location);
                const currentFloor = extractFloor(currentInfo.location);

                if (previousFloor !== null && currentFloor !== null && previousFloor !== currentFloor) {
                    addEventRef.current('moved', identity, previousFloor, currentFloor);
                    movedIdentities.add(identity);
                }
            }
        });

        // Update display
        updateDisplayRef.current(currentPeople, movedIdentities);

        // Update previous people
        previousPeopleRef.current = new Map(currentPeople);
    }, []);

    // Keep refs updated
    detectChangesRef.current = detectChanges;

    // Update event names in log
    const updateEventNames = useCallback(() => {
        setEventLog(prev => prev.map(event => ({
            ...event,
            name: formatPersonName(event.identity)
        })));
    }, [formatPersonName]);

    // Keep refs updated
    updateEventNamesRef.current = updateEventNames;

    useEffect(() => {
        // Prevent duplicate intervals
        if (peoplePollIntervalRef.current || devicesPollIntervalRef.current) {
            return;
        }

        let isMounted = true;

        const initialize = async () => {
            // Fetch people data first
            await fetchPeople();

            // Initial fetch
            const initialDevices = await fetchDevices();
            if (!isMounted) return;

            if (initialDevices && Array.isArray(initialDevices)) {
                const initialPeople = extractPeople(initialDevices);
                previousPeopleRef.current = new Map(initialPeople);

                // Don't log initial connections - only log changes after initial load
                if (updateDisplayRef.current) updateDisplayRef.current(initialPeople, new Set());
                setError(null);
            } else {
                setError('Failed to fetch initial device data');
            }

            // Periodically refresh people lookup (every 60 seconds)
            if (!peoplePollIntervalRef.current) {
                peoplePollIntervalRef.current = setInterval(async () => {
                    if (!isMounted) return;
                    await fetchPeople();
                    if (updateEventNamesRef.current) updateEventNamesRef.current();
                    if (updateDisplayRef.current) updateDisplayRef.current(previousPeopleRef.current, new Set());
                }, 60000);
            }

            // Start polling devices
            if (!devicesPollIntervalRef.current) {
                devicesPollIntervalRef.current = setInterval(async () => {
                    if (!isMounted) return;
                    // Skip if already fetching
                    if (isFetchingDevicesRef.current) {
                        return;
                    }
                    const currentDevices = await fetchDevices();
                    if (currentDevices !== null && Array.isArray(currentDevices)) {
                        if (detectChangesRef.current) detectChangesRef.current(currentDevices);
                        setError(null);
                    } else if (currentDevices === null && !isFetchingDevicesRef.current) {
                        // Only set error if fetch actually completed (not skipped)
                        setError('Failed to fetch devices');
                    }
                }, POLL_INTERVAL);
            }
        };

        initialize().catch(error => {
            if (!isMounted) return;
            console.error('Fatal error:', error);
            setError(`Fatal error: ${error.message}`);
        });

        return () => {
            isMounted = false;
            if (peoplePollIntervalRef.current) {
                clearInterval(peoplePollIntervalRef.current);
                peoplePollIntervalRef.current = null;
            }
            if (devicesPollIntervalRef.current) {
                clearInterval(devicesPollIntervalRef.current);
                devicesPollIntervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Update event names when nameLookup changes
    useEffect(() => {
        if (updateEventNamesRef.current) {
            updateEventNamesRef.current();
        }
    }, [nameLookup]);

    const sortedPeople = Array.from(connectedPeople.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const colSize = Math.ceil(sortedPeople.length / 3);
    const col1People = sortedPeople.slice(0, colSize);
    const col2People = sortedPeople.slice(colSize, colSize * 2);
    const col3People = sortedPeople.slice(colSize * 2);

    // Always show all 6 floors, even if they have 0 people
    const floorTotalsArray = [1, 2, 3, 4, 5, 6].map(floor => [
        floor,
        floorTotals.get(floor) || 0
    ]);
    // Cap the visualization scale at 25 people
    const maxCount = 25;

    const renderPeopleColumn = (people) => {
        return people.map(([identity, info]) => {
            const name = formatPersonName(identity);
            const locationText = info.location ? ` (${info.location})` : '';
            return (
                <div key={identity} className="person-item">
                    <span className="person-dot">●</span>
                    <span className="person-name">{name}</span>
                    {locationText && <span className="person-location">{locationText}</span>}
                </div>
            );
        });
    };

    // Debug function to simulate floor movement
    const simulateFloorMovement = useCallback(() => {
        const currentPeople = new Map(connectedPeople);
        if (currentPeople.size === 0) {
            alert('No people connected to simulate movement');
            return;
        }

        // Pick a random person
        const identities = Array.from(currentPeople.keys());
        const randomIdentity = identities[Math.floor(Math.random() * identities.length)];
        const personInfo = currentPeople.get(randomIdentity);

        const currentFloor = extractFloor(personInfo.location);
        if (currentFloor === null) {
            alert('Selected person has no floor location');
            return;
        }

        // Move to a different random floor
        let newFloor = currentFloor;
        while (newFloor === currentFloor) {
            newFloor = Math.floor(Math.random() * 6) + 1;
        }

        const newLocation = `${newFloor}${personInfo.location.substring(1)}`;
        const movedIdentities = new Set([randomIdentity]);

        // Update the person's location
        currentPeople.set(randomIdentity, { ...personInfo, location: newLocation });

        // Add event
        if (addEventRef.current) addEventRef.current('moved', randomIdentity, currentFloor, newFloor);

        // Update display with animation
        if (updateDisplayRef.current) updateDisplayRef.current(currentPeople, movedIdentities);

        // Update previous people ref
        previousPeopleRef.current = new Map(currentPeople);
    }, [connectedPeople]);

    // Debug function to add a person
    const addDebugPerson = useCallback(() => {
        const currentPeople = new Map(connectedPeople);

        // Generate a random identity
        const adjectives = ['happy', 'clever', 'swift', 'brave', 'calm', 'bright', 'wise', 'bold'];
        const nouns = ['developer', 'designer', 'manager', 'analyst', 'engineer', 'architect', 'consultant', 'specialist'];
        const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
        const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
        const randomNumber = Math.floor(Math.random() * 1000);
        const newIdentity = `debug.${randomAdjective}.${randomNoun}.${randomNumber}`;

        // Pick a random floor (1-6)
        const randomFloor = Math.floor(Math.random() * 6) + 1;
        const randomRoom = Math.floor(Math.random() * 50) + 1;
        const newLocation = `${randomFloor}${String(randomRoom).padStart(3, '0')}`;

        // Add the new person
        currentPeople.set(newIdentity, {
            location: newLocation,
            lastSeen: Date.now()
        });

        // Add event
        if (addEventRef.current) addEventRef.current('connected', newIdentity);

        // Update display
        if (updateDisplayRef.current) updateDisplayRef.current(currentPeople, new Set());

        // Update previous people ref
        previousPeopleRef.current = new Map(currentPeople);
    }, [connectedPeople]);

    return (
        <div className="dashboard-container">
            {error && (
                <div className="error">
                    ✗ {error}
                </div>
            )}

            <div className="status-bar">
                <div className="status-item">
                    <span className="status-label">Status:</span>
                    <span className="status-value">
                        {peopleCount} {peopleCount === 1 ? 'person' : 'people'} connected
                    </span>
                </div>
                <div className="status-item">
                    <span className="status-label">Last updated:</span>
                    <span className="status-time">{lastUpdate}</span>
                </div>
                <button
                    onClick={() => setTtsEnabled(!ttsEnabled)}
                    className={`dash-btn dash-btn-primary${ttsEnabled ? ' active' : ''}`}
                    style={{ marginLeft: 'auto', marginRight: urlParams.get('debug') ? '8px' : '0' }}
                    title={ttsEnabled ? 'Disable text-to-speech' : 'Enable text-to-speech'}
                >
                    {ttsEnabled ? 'TTS On' : 'TTS Off'}
                </button>
                {urlParams.get('debug') && (
                    <>
                        <button
                            onClick={simulateFloorMovement}
                            className="dash-btn dash-btn-ghost"
                            style={{ marginRight: '8px' }}
                        >
                            Simulate Movement
                        </button>
                        <button
                            onClick={addDebugPerson}
                            className="dash-btn dash-btn-ghost"
                        >
                            Add Person
                        </button>
                    </>
                )}
            </div>

            <div className="floor-chart">
                <div className="floor-chart-title">Floor Distribution:</div>
                {floorTotalsArray.map(([floor, count]) => {
                    // Cap the count at 25 for visualization, but show actual count
                    const cappedCount = Math.min(count, 25);
                    const percentage = maxCount > 0 ? (cappedCount / maxCount) * 100 : 0;
                    return (
                        <div key={floor} className="floor-bar">
                            <div className="floor-label">Floor {floor}:</div>
                            <div className="floor-bar-container">
                                <div
                                    className={`floor-bar-fill floor-${floor}`}
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                            <div className="floor-count">{count}</div>
                        </div>
                    );
                })}
            </div>

            <div className="main-content">
                <div className="section">
                    <div className="section-title">Event Log</div>
                    <div className="event-log">
                        {eventLog.length === 0 ? (
                            <div style={{ color: '#858585' }}>No events yet...</div>
                        ) : (
                            eventLog.slice().reverse().map((event, index) => {
                                let icon, typeClass, typeText;

                                if (event.type === 'connected') {
                                    icon = '✓';
                                    typeClass = 'connected';
                                    typeText = 'CONNECTED';
                                } else if (event.type === 'disconnected') {
                                    icon = '✗';
                                    typeClass = 'disconnected';
                                    typeText = 'DISCONNECTED';
                                } else if (event.type === 'moved') {
                                    icon = '→';
                                    typeClass = 'moved';
                                    typeText = 'MOVED';
                                } else {
                                    icon = '•';
                                    typeClass = '';
                                    typeText = event.type.toUpperCase();
                                }

                                const floorInfo = event.type === 'moved' && event.fromFloor !== null && event.toFloor !== null
                                    ? ` (${event.fromFloor}→${event.toFloor})`
                                    : '';

                                return (
                                    <div key={`${event.identity}-${event.timestamp}-${index}`} className="event-item">
                                        <span className={`event-icon ${typeClass}`}>{icon}</span>
                                        <span className={`event-type ${typeClass}`}>
                                            {typeText.padEnd(11)}
                                        </span>
                                        <span className="event-time">[{event.time}]</span>
                                        <span className="event-name">{event.name}</span>
                                        {floorInfo && <span className="event-floor">{floorInfo}</span>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
                <div className="section">
                    <div className="section-title">Connected People</div>
                    <div className="people-container">
                        <div className="people-column">
                            {col1People.length === 0 && sortedPeople.length === 0 ? (
                                <div style={{ color: '#858585' }}>No one connected</div>
                            ) : (
                                renderPeopleColumn(col1People)
                            )}
                        </div>
                        <div className="people-column">
                            {renderPeopleColumn(col2People)}
                        </div>
                        <div className="people-column">
                            {renderPeopleColumn(col3People)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="floors-grid">
                {[1, 2, 3, 4, 5, 6].map(floor => {
                    const people = floorsData.get(floor) || [];
                    return (
                        <div key={floor} className="floor-column">
                            <div className={`floor-column-title floor-${floor}`}>
                                Floor {floor}
                            </div>
                            {people.length === 0 ? (
                                <div className="floor-empty">(empty)</div>
                            ) : (
                                people.map(([identity, info]) => {
                                    const name = formatPersonName(identity);
                                    const isRecentlyMoved = recentlyMoved.has(identity);
                                    return (
                                        <div
                                            key={identity}
                                            className={`person-item ${isRecentlyMoved ? 'person-moved' : ''}`}
                                        >
                                            <span className="person-dot">●</span>
                                            <span className="person-name">{name}</span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default Dashboard;

